import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';

export type UserRole = 'coach' | 'client' | 'admin' | 'conversation_partner' | null;

export function useUserRole() {
  const { user } = useAuth();
  const [role, setRole] = useState<UserRole>(null);
  const [allRoles, setAllRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setRole(null);
      setAllRoles([]);
      setLoading(false);
      return;
    }

    const fetchRole = async () => {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error fetching user role:', error);
        setRole(null);
        setAllRoles([]);
      } else if (data && data.length > 0) {
        // Store all roles
        const roles = data.map(r => r.role);
        setAllRoles(roles);
        
        // Set primary role: admin > coach > client > conversation_partner
        if (roles.includes('admin')) {
          setRole('admin');
        } else if (roles.includes('coach')) {
          setRole('coach');
        } else if (roles.includes('client')) {
          setRole('client');
        } else if (roles.includes('conversation_partner')) {
          setRole('conversation_partner');
        } else {
          setRole(null);
        }
      } else {
        setRole(null);
        setAllRoles([]);
      }
      setLoading(false);
    };

    fetchRole();
  }, [user]);

  return { 
    role, 
    allRoles,
    loading, 
    isCoach: allRoles.includes('coach'), 
    isClient: allRoles.includes('client'),
    isAdmin: allRoles.includes('admin'),
    isConversationPartner: allRoles.includes('conversation_partner')
  };
}
